import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Image, LoadingIndicator, SearchBar, Typography, DropdownButton, Pagination } from 'lib/components';
import { NursesDataTable } from 'lib/components/DataTables';
import {
	useAppDispatch,
	actions,
	useSelectNurses,
	useSelectAuthorization,
	useSelectRequest,
	RequestManager,
} from 'store';
import { DropdownItem } from 'lib/components/types';
import { EmployeeType, NurseEmployeeType as NET } from 'lib/util';
import routes from 'lib/api/routes';
import { useComprehensiveTopNav, useWindowResize, useCustomToast, usePagination, useSearch } from 'hooks';
import classes from './index.module.scss';

/**
 * Dropdown options for filtering nurses by employee type
 * Used in the employee type filter dropdown component
 */
const POSITION_DROPDOWN_ITEMS: DropdownItem<number>[] = [
	{ label: NET.text(EmployeeType.FULL_TIME), value: EmployeeType.FULL_TIME },
	{ label: NET.text(EmployeeType.PART_TIME), value: EmployeeType.PART_TIME },
	{ label: NET.text(EmployeeType.CONTRACTOR), value: EmployeeType.CONTRACTOR },
];

/**
 * Nurses management screen component
 * Displays a list of nurses with filtering, search, and management capabilities
 * Allows creating and deleting nurses based on user permissions
 */
const NursesScreen = () => {
	// Navigation helper
	const { push } = useHistory();
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Responsive design helpers
	const { isSmallDevice, isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Permission checks for nurse management
	const { canCreateNurse, canDeleteNurse } = useSelectAuthorization();
	// Nurse data from Redux store
	const nurses = useSelectNurses();

	// Selected employee type filter value (-1 means no filter)
	const [selectedPositionDropdownItem, setSelectedPositionDropdownItem] = useState<number>(-1);

	// Search query for filtering nurses
	const [query, setQuery] = useState('');
	// ID of the currently selected nurse (for actions like delete)
	const [checked, setChecked] = useState<number[]>([]);

	/**
	 * Fetch nurses data and BuddyPunch employees on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchNurses());
		dispatch(actions.getBuddyPunchEmployees());
	}, []);

	/**
	 * Monitor API requests for nurse deletion
	 * Shows toast notifications for success/failure and resets selection
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.NURSE_ID(checked[0]), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				setChecked([]);
			}
		}
	}, [active]);

	/**
	 * Set up pagination for nurses list
	 * Returns data and handlers for paginated display
	 */
	const { isDataFetched, listing, setData, paginatedData, paginationProps } = usePagination(nurses, {});

	/**
	 * Handle search functionality using custom hook
	 * Searches across nurse names
	 */
	useSearch(
		query,
		// Reset function - called when query is empty
		() => {
			setData(listing);
		},
		// Search function - called when query has content
		() => {
			const result = listing.filter((nurse) => nurse.user.name.toLowerCase().search(query.toLowerCase()) !== -1);
			setData(result);
		}
	);

	/**
	 * Filter nurses by selected employee type when position filter changes
	 * Only applies when an employee type is selected from the dropdown
	 */
	useEffect(() => {
		if (selectedPositionDropdownItem === -1) return;

		const result = listing.filter((nurse) => nurse.employeeType === selectedPositionDropdownItem);
		setData(result);
	}, [selectedPositionDropdownItem]);

	/**
	 * Handle checkbox selection for nurse rows
	 * Only one nurse can be selected at a time for actions
	 *
	 * @param id - ID of the nurse being checked/unchecked
	 * @param isChecked - Whether the checkbox is being checked or unchecked
	 */
	const handleCheckAction = (id: number, isChecked: boolean) => {
		if (isChecked && !checked.includes(id)) {
			setChecked([id]);
			return;
		}

		if (!isChecked || checked.includes(id)) {
			setChecked([]);
		}
	};

	/**
	 * Handle nurse deletion action
	 * Dispatches delete action for the selected nurse
	 */
	const handleDelete = () => {
		dispatch(actions.deleteNurse(checked[0]));
	};

	/**
	 * Handle position filter dropdown selection
	 *
	 * @param value - Selected employee type ID from the dropdown
	 */
	const handlePositionDropdownItemSelect = (value: number) => {
		setSelectedPositionDropdownItem(value);
	};

	// Set up the top navigation bar with title
	useComprehensiveTopNav('Nurses');

	// Show loading indicator while data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<div className={classes['nurses-screen-container']}>
			<div className={classes['row']}>
				<SearchBar
					query={query}
					setQuery={setQuery}
					onSearch={() => console.log({ query })}
					className={classes['search-input-container']}
					placeholder="Search..."
				/>
				{canCreateNurse && (
					<button
						className={classes['row']}
						style={{
							marginTop: '1rem',
						}}
						onClick={() => push('/nurses/create')}
					>
						<Typography variant={isSmallDevice ? 'h5' : 'h6'} weight="medium">
							Add New Nurse
						</Typography>
						<div className={classes['plus-icon']}>+</div>
					</button>
				)}
			</div>
			<div
				className={classes['row']}
				style={{
					marginTop: '1.2rem',
					alignItems: 'center',
				}}
			>
				<div
					style={{
						marginRight: '1rem',
					}}
				>
					<Typography weight="medium" variant={isSmallDevice ? 'h5' : 'h6'}>
						Filter by:
					</Typography>
				</div>
				<div
					style={{
						marginRight: '1rem',
					}}
				>
					<DropdownButton
						title="Employee Type"
						list={POSITION_DROPDOWN_ITEMS}
						onItemSelect={handlePositionDropdownItemSelect}
					/>
				</div>
				{/* <div
					style={{
						marginRight: '1rem',
					}}
				>
					<DropdownButton
						title="Status"
						list={STATUS_DROPDOWN_ITEMS}
						// onItemSelect={handleStatusDropdownItemSelect}
					/>
				</div> */}
			</div>
			<div className={classes['row']} style={{ justifyContent: 'space-between' }}>
				<div className={classes['row']} style={{ marginTop: isSmallerDevice ? 0 : '0.5rem' }}>
					{checked.length !== 0 && (
						<>
							{canDeleteNurse && (
								<button
									className={classes['action-button']}
									style={{
										backgroundColor: '#FCD3D1',
										borderColor: '#FF3B30',
									}}
									onClick={handleDelete}
								>
									<Image
										src="icTrashDanger"
										style={{
											marginRight: '0.5rem',
										}}
									/>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="danger">
										Delete
									</Typography>
								</button>
							)}
						</>
					)}
				</div>
			</div>
			<NursesDataTable nurses={paginatedData} checkedId={checked[0] ?? -1} checkAction={handleCheckAction} />
			<Pagination {...paginationProps} />
		</div>
	);
};

export default NursesScreen;
